/**
 * Phase 3 — Digital SOA structure appendix (trusts / companies).
 * Spec §10, CLIENT_STRUCTURES_ENTITY_MODELLING_SPEC.md.
 */
import {
    entityOwnershipValue,
    getStructureTypeLabel,
    isEntityOwnership,
} from '../../../shared/src/calculation/constants/clientStructureTypes.ts';
import {
    findAssetInFactFind,
    isStructureFundingIncludedInSuggestions,
    parseRadFundingAssetKey,
} from './entityFundingUtils.ts';
import { getSelectedInvestmentProperty } from './entityInvestmentPropertySale.ts';
import type { RadFundingMap, RadFundingScenario } from './suggestionEngineFunding.ts';
import type { InvestmentPropertySaleDetails } from './entityInvestmentPropertySale.ts';
import type { DraftFactFind } from '../constants/initialFactFind.ts';

function prettifyAssetType(typeId: string): string {
    const labels: Record<string, string> = {
        bankAccounts: 'Bank account',
        termDeposits: 'Term deposit',
        shares: 'Shares / managed funds',
        investmentBonds: 'Investment bond',
        accountBasedPensions: 'Account-based pension',
        investmentProperty: 'Investment property',
        funeralBonds: 'Funeral bond',
        prepaidFuneral: 'Prepaid funeral',
        loansToOthers: 'Money lent to others',
        radPaid: 'RAD paid',
    };
    return labels[typeId] || String(typeId || '').replace(/([A-Z])/g, ' $1').trim();
}

function activeStructures(factFind: DraftFactFind | null | undefined) {
    return (factFind?.structures || []).filter((s) => {
        const client = s.apportionmentClient || 0;
        const partner = s.apportionmentPartner || 0;
        return client > 0 || partner > 0;
    });
}

type ActiveStructure = ReturnType<typeof activeStructures>[number];

interface EntityAssetRow {
    structureName: string;
    assetType: string;
    description: string;
    value: number;
}

function collectEntityAssetRows(factFind: DraftFactFind | null | undefined, structures: ActiveStructure[]): EntityAssetRow[] {
    const rows: EntityAssetRow[] = [];
    structures.forEach((structure) => {
        const entityOwn = entityOwnershipValue(structure.id ?? '');
        const structureName = structure.name?.trim() || 'Trust or company';

        Object.entries(factFind?.assets || {}).forEach(([assetType, list]) => {
            (list || []).forEach((asset) => {
                if (asset?.ownership !== entityOwn) return;
                const value = asset.value || 0;
                if (value <= 0) return;
                rows.push({
                    structureName,
                    assetType: prettifyAssetType(assetType),
                    description: asset.description || prettifyAssetType(assetType),
                    value,
                });
            });
        });
    });
    return rows.sort(
        (a, b) =>
            a.structureName.localeCompare(b.structureName) ||
            a.assetType.localeCompare(b.assetType)
    );
}

function radFundingUsesEntityAssets(factFind: DraftFactFind | null | undefined, radFunding: RadFundingMap | null | undefined): boolean {
    if (!radFunding || typeof radFunding !== 'object') return false;

    const entries: Array<{ key: string; assetId?: string | number; amount?: number | string }> = [];
    Object.entries(radFunding).forEach(([key, value]) => {
        if (key.includes('-') && !Array.isArray(value) && (value === undefined || typeof value !== 'object')) {
            entries.push({ key, amount: value as number | string | undefined });
            return;
        }
        const list = Array.isArray(value) ? value : value ? [value] : [];
        list.forEach((row) => {
            if (row?.id != null) entries.push({ key, assetId: row.id, amount: row.amount });
        });
    });

    return entries.some((entry) => {
        let assetType = entry.key;
        let assetId = entry.assetId;
        if (!assetId) {
            const parsed = parseRadFundingAssetKey(entry.key);
            if (!parsed) return false;
            assetType = parsed.assetType;
            assetId = parsed.assetId;
        }
        const located = findAssetInFactFind(factFind, assetType, assetId);
        return located?.asset && isEntityOwnership(located.asset.ownership);
    });
}

interface SelectedStrategyLike extends RadFundingScenario {
    inputs?: { radFunding?: RadFundingMap };
    investmentPropertyDetails?: InvestmentPropertySaleDetails;
}

/**
 * Whether the selected strategy modelled drawdowns or sales from entity-owned assets.
 */
export function selectedStrategyUsesEntityFunding(
    factFind: DraftFactFind | null | undefined,
    selectedStrategy: SelectedStrategyLike | null | undefined,
): boolean {
    if (!selectedStrategy) return false;
    if (selectedStrategy.structureFundingIncluded) return true;
    if (selectedStrategy.strategyOptions?.structureFundingIncluded) return true;

    if (radFundingUsesEntityAssets(factFind, selectedStrategy.radFunding)) return true;
    if (radFundingUsesEntityAssets(factFind, selectedStrategy.inputs?.radFunding)) return true;

    const ipDetails =
        selectedStrategy.strategyOptions?.investmentPropertyDetails ||
        selectedStrategy.investmentPropertyDetails;
    if (selectedStrategy.strategyOptions?.sellInvestmentProperty && ipDetails) {
        const property = getSelectedInvestmentProperty(factFind, ipDetails);
        if (property && isEntityOwnership(property.ownership)) return true;
    }

    return false;
}

export interface StructureAppendixOptions {
    selectedStrategy?: SelectedStrategyLike | null;
    strategySource?: 'manual_control' | 'suggestion_engine' | string;
}

export interface DigitalSoaStructureAppendix {
    structureRows: Array<{ name: string; typeLabel: string; apportionmentClient: number; apportionmentPartner: number }>;
    entityAssetRows: EntityAssetRow[];
    suggestionGate: string;
    includedInSuggestions: boolean;
    strategySource: string;
    selectedUsedEntityFunding: boolean;
    automaticSuggestionsNote: string;
    selectedStrategyNote: string;
    legalNote: string;
    apportionmentDisclaimer: string;
}

/**
 * @returns Appendix payload when structures are registered; else null.
 */
export function buildDigitalSoaStructureAppendix(
    factFind: DraftFactFind | null | undefined,
    options: StructureAppendixOptions = {},
): DigitalSoaStructureAppendix | null {
    const structures = activeStructures(factFind);
    if (!structures.length) return null;

    const { selectedStrategy, strategySource = 'suggestion_engine' } = options;
    const includedInSuggestions = isStructureFundingIncludedInSuggestions(factFind);
    const selectedUsedEntityFunding = selectedStrategyUsesEntityFunding(factFind, selectedStrategy);

    let automaticSuggestionsNote: string;
    if (strategySource === 'manual_control') {
        automaticSuggestionsNote =
            'This advice was prepared from <strong>manual control</strong> selections you made explicitly. The suggestion-engine setting for trust or company assets does not restrict manual funding choices.';
    } else if (includedInSuggestions) {
        automaticSuggestionsNote =
            'In Strategy Analysis Setup you chose to <strong>include</strong> trust or company assets when the suggestion engine ranked automatic funding options (liquid assets and whole investment property sales).';
    } else {
        automaticSuggestionsNote =
            'In Strategy Analysis Setup you chose <strong>personal assets only</strong> for automatic strategy suggestions (default). Entity arrangements were still included in Age Pension and aged care fee estimates.';
    }

    let selectedStrategyNote: string;
    if (selectedUsedEntityFunding) {
        selectedStrategyNote =
            'Your <strong>selected strategy</strong> modelled funding from trust or company assets (for example entity bank, shares, or a whole entity-owned property sale).';
    } else if (strategySource === 'manual_control') {
        selectedStrategyNote =
            'Your <strong>selected manual strategy</strong> did not model drawdowns from trust or company asset pools in the funding mix captured for this SOA.';
    } else {
        selectedStrategyNote =
            'Your <strong>selected strategy</strong> did not model drawdowns from trust or company asset pools in the funding mix captured for this SOA.';
    }

    return {
        structureRows: structures.map((s) => ({
            name: s.name?.trim() || 'Trust or company',
            typeLabel: getStructureTypeLabel(s.type || ''),
            apportionmentClient: s.apportionmentClient || 0,
            apportionmentPartner: s.apportionmentPartner || 0,
        })),
        entityAssetRows: collectEntityAssetRows(factFind, structures),
        suggestionGate: factFind?.structureFundingInSuggestions || 'exclude',
        includedInSuggestions,
        strategySource,
        selectedUsedEntityFunding,
        automaticSuggestionsNote,
        selectedStrategyNote,
        legalNote:
            'Care Companion does not determine whether a trustee or company may sell assets or make distributions. Confirm permissibility under the trust deed, company constitution, tax treatment, and for all owners or beneficiaries before implementation.',
        apportionmentDisclaimer:
            'Apportionment percentages are user-supplied for modelling only and are not a Centrelink or ATO determination.',
    };
}

/**
 * @param appendix
 * @param formatMoney
 */
export function buildDigitalSoaStructureAppendixHtml(
    appendix: DigitalSoaStructureAppendix | null | undefined,
    formatMoney: (n: number) => string,
): string {
    if (!appendix) return '';

    const structureTable = `
<table>
  <thead><tr><th>Structure</th><th>Type</th><th>Client %</th><th>Partner %</th></tr></thead>
  <tbody>
    ${appendix.structureRows
        .map(
            (row) =>
                `<tr><td>${row.name}</td><td>${row.typeLabel}</td><td>${row.apportionmentClient}%</td><td>${row.apportionmentPartner}%</td></tr>`
        )
        .join('')}
  </tbody>
</table>`;

    const assetTable =
        appendix.entityAssetRows.length > 0
            ? `
<div class="h3">Entity-owned assets in fact-find (summary)</div>
<table>
  <thead><tr><th>Structure</th><th>Asset type</th><th>Description</th><th>Value</th></tr></thead>
  <tbody>
    ${appendix.entityAssetRows
        .map(
            (row) =>
                `<tr><td>${row.structureName}</td><td>${row.assetType}</td><td>${row.description}</td><td>${formatMoney(row.value)}</td></tr>`
        )
        .join('')}
  </tbody>
</table>`
            : '<p class="muted small">No entity-tagged asset balances were recorded in the fact-find at the time of export.</p>';

    return `
<div class="h3">Trusts, companies &amp; related entities</div>
<p class="small muted">${appendix.apportionmentDisclaimer}</p>
${structureTable}
${assetTable}
<div class="callout callout--amber" style="margin-top:14px">
  <p class="callout-title">Suggestion engine &amp; entity funding</p>
  <p>${appendix.automaticSuggestionsNote}</p>
  <p style="margin-bottom:0">${appendix.selectedStrategyNote}</p>
</div>
<p class="small muted" style="margin-top:10px"><strong>Important:</strong> ${appendix.legalNote}</p>`;
}
